/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import { FloatingPortal, useMergeRefs } from '@floating-ui/react';
import classNames from 'classnames';
import React, { useEffect } from 'react';

import { type PolymorphicCommonProps } from '../_common/types';
import { ConditionalWrap } from '../conditional-wrap';
import { useSpotlightContext } from './SpotlightProvider';
import {
  type IndicatorPlacement,
  type IndicatorVariant,
  useIndicator,
} from './use-indicator';

type SpotlightTargetProps = {
  /** The id of the wrapper. This should match a `target` prop in a `Spotlight`, to indicate that this is where it should be rendered when open */
  id: string;
  /** A boolean that controls if the pulse animation should be used or not */
  hasPulse?: boolean;
  /** A string that controls the style of the indicator. It can be either "point" or "border" */
  indicatorVariant?: IndicatorVariant;
  /** A string that controls the placement of the indicator. A combination of "middle", "top", "right", "bottom" and "left" separated by a dash, e.g. "top-right" */
  indicatorPlacement?: IndicatorPlacement;
  /** An inline style CSS property to control the border radius of the wrapper */
  borderRadius?: React.CSSProperties['borderRadius'];
  /** The content to wrap */
  children?: React.ReactNode;
  /** Fit the target size to the children. */
  shouldFitToChildren?: boolean;
  /**
   * When true, merges the spotlight target props directly with the child element instead of wrapping it in a div.
   *
   * This is useful for applying spotlight functionality to existing interactive elements (buttons, links, etc.)
   * while preserving their original behavior, styling, and accessibility attributes.
   *
   * @example
   * ```tsx
   * <SpotlightTarget id="my-button" asChild>
   *   <FilledButton>Click me</FilledButton>
   * </SpotlightTarget>
   * ```
   *
   * @warning Only use with interactive elements that can receive focus and handle click events
   * @warning The child element's `id` will be overridden with the target's `id`
   */
  asChild?: boolean;
};

export const SpotlightTarget = ({
  id,
  hasPulse = true,
  indicatorVariant = 'border',
  indicatorPlacement = 'top-right',
  borderRadius,
  shouldFitToChildren = false,
  children,
  className,
  htmlAttributes,
  style,
  as,
  ref,
  asChild,
  ...restProps
}: PolymorphicCommonProps<'div', SpotlightTargetProps>) => {
  const Component = as ?? 'div';

  const {
    isActiveSpotlight,
    isOpen,
    setIsOpen,
    registerTarget,
    unregisterTarget,
    setActiveSpotlight,
  } = useSpotlightContext();

  const internalRef = React.useRef<HTMLElement>(null);
  const mergedRefs = useMergeRefs<HTMLElement>([
    ref as React.RefObject<HTMLElement>,
    internalRef as React.RefObject<HTMLElement>,
  ]);
  useEffect(() => {
    if (internalRef.current !== null) {
      registerTarget(id, internalRef as React.RefObject<HTMLElement>);
    }
    return () => unregisterTarget(id);
  }, [id, registerTarget, unregisterTarget]);

  const isActive = isActiveSpotlight(id);
  const indicator = useIndicator({
    hasPulse,
    indicatorPlacement,
    indicatorVariant,
    isOpen: isActive,
    targetRef: internalRef,
  });

  if (asChild === true && React.isValidElement(children)) {
    const child = children as React.ReactElement<{
      className?: string;
      style?: React.CSSProperties;
      onClick?: (e: React.MouseEvent<HTMLElement>) => void;
      onKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void;
    }>;

    const onClick = (e: React.MouseEvent<HTMLElement>) => {
      if (isActive) {
        // When spotlight is active, always prevent the child's click handler
        e.preventDefault();

        if (!isOpen) {
          setIsOpen(true);
        }
        // Don't call child.props?.onClick when spotlight is active
        return;
      }

      // Only call the child's click handler when spotlight is not active
      child.props?.onClick?.(e);
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
      if (isActive && !isOpen && (e.code === 'Enter' || e.code === 'Space')) {
        e.preventDefault();
        setIsOpen(true);
        return;
      }

      if (isActive && e.code === 'Escape') {
        e.preventDefault();
        setActiveSpotlight(null);
        return;
      }

      child.props?.onKeyDown?.(e);
    };

    // IMPORTANT: do NOT force role/tabIndex here; preserves Composite semantics for the SideNavigation
    const mergedProps = {
      id,
      onClick,
      onKeyDown,
      ref: mergedRefs,
      style: {
        ...style,
        ...child.props.style,
      },
      // Avoid overriding the child's own role/tabIndex with any passed htmlAttributes
      ...(htmlAttributes
        ? { ...htmlAttributes, role: undefined, tabIndex: undefined }
        : {}),
    };

    return (
      <>
        {React.cloneElement(child, mergedProps as Record<string, unknown>)}
        <ConditionalWrap
          shouldWrap={isActive && isOpen}
          wrap={(wrapChildren) => (
            <FloatingPortal>{wrapChildren}</FloatingPortal>
          )}
        >
          {indicator.context.open && (
            <div
              className={indicator.indicatorClasses}
              ref={indicator.context.refs.setFloating}
              style={{ ...indicator.floatingStyles, ...indicator.style }}
            />
          )}
        </ConditionalWrap>
      </>
    );
  }

  const classes = classNames(
    'ndl-spotlight-target',
    {
      'ndl-spotlight-target-fit-to-children': shouldFitToChildren,
      'ndl-spotlight-target-open': isOpen && isActive,
    },
    className,
  );

  const inertClasses = classNames(
    'ndl-spotlight-target-inert',
    {
      'ndl-spotlight-target-inert-fit-to-children': shouldFitToChildren,
    },
    className,
  );

  return (
    <>
      <Component
        role={isActive ? 'button' : undefined}
        id={id}
        ref={mergedRefs}
        className={classes}
        onClick={(e) => {
          if (isActive) {
            e.preventDefault();
            e.stopPropagation();

            if (!isOpen) {
              setIsOpen(true);
            }
          }
        }}
        style={{ borderRadius, ...style }}
        onKeyDown={(e) => {
          if (
            isActive &&
            !isOpen &&
            (e.code === 'Enter' || e.code === 'Space')
          ) {
            setIsOpen(true);
            e.preventDefault();
          } else if (e.code === 'Escape' && isActive) {
            setActiveSpotlight(null);
          }
        }}
        tabIndex={isActive ? 0 : undefined}
        {...(isActive && { 'aria-label': 'Open spotlight' })}
        {...restProps}
        {...htmlAttributes}
      >
        <ConditionalWrap
          shouldWrap={isActive}
          wrap={(children) => (
            <div inert aria-disabled className={inertClasses}>
              {children}
            </div>
          )}
        >
          {children}
        </ConditionalWrap>
      </Component>
      <ConditionalWrap
        shouldWrap={isActive && isOpen}
        wrap={(wrapChildren) => <FloatingPortal>{wrapChildren}</FloatingPortal>}
      >
        {indicator.context.open && (
          <div
            className={indicator.indicatorClasses}
            ref={indicator.context.refs.setFloating}
            style={{ ...indicator.floatingStyles, ...indicator.style }}
          />
        )}
      </ConditionalWrap>
    </>
  );
};
